import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  FormControlLabel
} from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import styles from './AdminConfigTractorNozzleProfiles.module.css';
import AddNozzleProfileDialog from './AddNozzleProfileDialog/AddNozzleProfileDialog';
import EditNozzleProfileDialog from './EditNozzleProfileDialog/EditNozzleProfileDialog';

const AdminConfigTractorNozzleProfiles = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tractor, setTractor] = useState({});
  const [nozzleProfiles, setNozzleProfiles] = useState([]);
  const [fetchedNozzleProfiles, setFetchedNozzleProfiles] = useState([]);
  const [nozzleProfileForDeletion, setNozzleProfileForDeletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(false);
  const [addNozzleProfileDialogOpen, setIsAddNozzleProfileDialogOpen] = useState(false);

  // New state for booms and nozzles
  const [booms, setBooms] = useState([]);
  const [nozzles, setNozzles] = useState([]);

  // Add state for edit dialog
  const [editNozzleProfileDialogOpen, setEditNozzleProfileDialogOpen] = useState(false);
  const [editNozzleProfileId, setEditNozzleProfileId] = useState(null);

  const getClientId = useCallback(
    () => searchParams.get('clientId'),
    [searchParams]
  );

  const getTractorId = useCallback(
    () => searchParams.get('tractorId'),
    [searchParams]
  );

  const fetchTractor = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractor?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const tractor = await response.json();
      setTractor(tractor);
      setLoading(false);
      setError(false);
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
    }
  }, [getClientId, getTractorId]);

  const fetchNozzleProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/nozzle-profiles?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const nozzleProfiles = (await response.json()).map((n) => ({
        ...n,
        overlap: Math.round(n.overlap * 100)
      }));
      setFetchedNozzleProfiles(nozzleProfiles);
      setNozzleProfiles(nozzleProfiles);
      setLoading(false);
      setError(false);
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
    }
  }, [getClientId, getTractorId]);

  // Fetch booms for a tractor
  const fetchBooms = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractor/booms?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const booms = await response.json();
      setBooms(booms);
      setLoading(false);
      setError(false);
      return booms;
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
      setBooms([]);
      return [];
    }
  }, [getClientId, getTractorId]);

  // Fetch nozzles for a tractor
  const fetchNozzles = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractor/nozzles?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const nozzles = await response.json();
      setNozzles(nozzles);
      setLoading(false);
      setError(false);
      return nozzles;
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
      setNozzles([]);
      return [];
    }
  }, [getClientId, getTractorId]);

  useEffect(() => {
    fetchTractor();
    fetchNozzleProfiles();
    fetchBooms();
    fetchNozzles();
  }, [getClientId, getTractorId, fetchTractor, fetchNozzleProfiles, fetchBooms, fetchNozzles]);

  const onEditNozzleProfile = (nozzleProfileId, fieldName, newValue) => {
    const updatedNozzleProfiles = nozzleProfiles.map((nozzleProfile) => {
      if (nozzleProfile.id === nozzleProfileId) {
        return {
          ...nozzleProfile,
          [fieldName]: newValue
        };
      }
      return nozzleProfile;
    });
    setNozzleProfiles(updatedNozzleProfiles);
  };

  const hasNozzleProfileValueChanged = (sprayNozzle, fieldName) => {
    const fetchedNozzleProfile = fetchedNozzleProfiles.find(
      (fnp) => fnp.id === sprayNozzle.id
    );
    return fetchedNozzleProfile[fieldName] !== sprayNozzle[fieldName];
  };

  const haveAnyValuesChanged = () => {
    return _.isEqual(fetchedNozzleProfiles, nozzleProfiles);
  };

  const discardChanges = () => {
    setNozzleProfiles(fetchedNozzleProfiles);
  };

  const isValid = () => {
    const numOfDefaultProfiles = nozzleProfiles.filter(profile => profile.isDefault).length;

    if (numOfDefaultProfiles > 1) {
      toast.error('There can only be at most 1 default nozzle profile.');
      return false;
    }
    return true;
  };

  const updateNozzleProfiles = async () => {
    if (!isValid()) {
      return;
    }

    const token = await authService.getAccessToken();
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/nozzle-profiles`,
      {
        method: 'POST',
        headers: !token
          ? {}
          : {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        body: JSON.stringify(nozzleProfiles)
      }
    );

    if (response.ok) {
      toast.success('Nozzle profile updated successfully!');
    } else {
      toast.error(`${response.status} ${response.statusText}`);
    }
    fetchNozzleProfiles();
  };

  const openAddNozzleProfileDialog = () => {
    setIsAddNozzleProfileDialogOpen(true);
  };

  const closeShowAddNozzleProfileDialog = () => {
    setIsAddNozzleProfileDialogOpen(false);
  };

  const handleAddNozzleProfileSuccess = () => {
    setIsAddNozzleProfileDialogOpen(false);
    fetchNozzleProfiles();
  };

  // Handler to open edit dialog
  const openEditNozzleProfileDialog = (profileId) => {
    setEditNozzleProfileId(profileId);
    setEditNozzleProfileDialogOpen(true);
  };

  // Handler to close edit dialog
  const closeEditNozzleProfileDialog = () => {
    setEditNozzleProfileDialogOpen(false);
    setEditNozzleProfileId(null);
  };

  // Handler for successful edit
  const handleEditNozzleProfileSuccess = () => {
    setEditNozzleProfileDialogOpen(false);
    setEditNozzleProfileId(null);
    fetchNozzleProfiles();
  };

  // Mobile Card View
  const renderMobileView = () => (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {nozzleProfiles.map((nozzleProfile, index) => (
        <Card key={nozzleProfile.id} variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Profile #{index + 1}
              </Typography>

              <TextField
                label="Name"
                type="text"
                fullWidth
                value={nozzleProfile.name}
                error={hasNozzleProfileValueChanged(nozzleProfile, 'name')}
                onChange={(e) =>
                  onEditNozzleProfile(
                    nozzleProfile.id,
                    'name',
                    e.target.value
                  )
                }
              />

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Active Nozzle Count
                </Typography>
                <Typography variant="body1">
                  {nozzleProfile.nozzles.length}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Spray Width (in)
                </Typography>
                <Typography variant="body1">
                  {
                    nozzleProfile.nozzles && nozzleProfile.nozzles.length > 0
                      ? (
                        nozzleProfile.nozzles.reduce(
                          (sum, nozzle) => sum + (parseFloat(nozzle.sprayWidth) || 0),
                          0
                        )
                      ).toFixed(1)
                      : "0.0"
                  }
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={nozzleProfile.isDefault}
                    onChange={(e) =>
                      onEditNozzleProfile(
                        nozzleProfile.id,
                        'isDefault',
                        e.target.checked
                      )
                    }
                  />
                }
                label="Is Default"
              />

              <Button
                variant="outlined"
                fullWidth
                startIcon={<EditIcon />}
                disabled={!haveAnyValuesChanged()}
                onClick={() => openEditNozzleProfileDialog(nozzleProfile.id)}
              >
                Edit Profile
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <TableContainer component={Paper} sx={{ my: 1 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Active Nozzle Count</TableCell>
            <TableCell>Spray Width (in)</TableCell>
            <TableCell>Is Default</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nozzleProfiles.map((nozzleProfile, index) => (
            <TableRow key={nozzleProfile.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <TextField
                  type="text"
                  value={nozzleProfile.name}
                  error={hasNozzleProfileValueChanged(nozzleProfile, 'name')}
                  onChange={(e) =>
                    onEditNozzleProfile(
                      nozzleProfile.id,
                      'name',
                      e.target.value
                    )
                  }
                />
              </TableCell>
              <TableCell>
                <Typography>
                  {nozzleProfile.nozzles.length}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  {
                    nozzleProfile.nozzles && nozzleProfile.nozzles.length > 0
                      ? (
                        nozzleProfile.nozzles.reduce(
                          (sum, nozzle) => sum + (parseFloat(nozzle.sprayWidth) || 0),
                          0
                        )
                      ).toFixed(1)
                      : "0.0"
                  }
                </Typography>
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={nozzleProfile.isDefault}
                  onChange={(e) =>
                    onEditNozzleProfile(
                      nozzleProfile.id,
                      'isDefault',
                      e.target.checked
                    )
                  }
                />
              </TableCell>
              <TableCell>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  disabled={!haveAnyValuesChanged()}
                  onClick={() => openEditNozzleProfileDialog(nozzleProfile.id)}
                >
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
        <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
          {tractor.name}
        </Typography>
        <Typography variant="h5" sx={{ pb: 2 }}>
          Nozzle Profile Management
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography>
              Displays all the nozzle profiles associated with the sprayer.
            </Typography>
          </Box>
          {haveAnyValuesChanged() ? (
            <Button
              variant="outlined"
              disabled={loading}
              startIcon={<AddIcon />}
              onClick={openAddNozzleProfileDialog}
              fullWidth={isMobile}
            >
              Add Nozzle Profile
            </Button>
          ) : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                startIcon={<UndoIcon />}
                onClick={discardChanges}
                fullWidth={isMobile}
              >
                Discard Changes
              </Button>
              <Button
                variant="outlined"
                onClick={updateNozzleProfiles}
                startIcon={<SaveIcon />}
                fullWidth={isMobile}
              >
                Save Changes
              </Button>
            </Stack>
          )}
        </Stack>

        {isMobile ? renderMobileView() : renderDesktopView()}

        {addNozzleProfileDialogOpen && (
          <AddNozzleProfileDialog
            clientId={getClientId()}
            tractorId={getTractorId()}
            open={addNozzleProfileDialogOpen}
            onClose={closeShowAddNozzleProfileDialog}
            onSuccess={handleAddNozzleProfileSuccess}
          />
        )}
        {editNozzleProfileDialogOpen && (
          <EditNozzleProfileDialog
            clientId={getClientId()}
            tractorId={getTractorId()}
            nozzleProfileId={editNozzleProfileId}
            open={editNozzleProfileDialogOpen}
            onClose={closeEditNozzleProfileDialog}
            onSuccess={handleEditNozzleProfileSuccess}
          />
        )}
      </Box>
    </>
  );
};

export default AdminConfigTractorNozzleProfiles;